import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { USER_ROLES } from '../utils/roles';
import { Users, Shield, User, Crown, X, Trash2, AlertCircle, Filter } from 'lucide-react';

export default function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showOnlyRecent, setShowOnlyRecent] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Filter users based on the toggle
    if (showOnlyRecent) {
      // Show only users who signed in within the last 90 days
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const recent = users.filter(user => {
        // If user has a lastLoginAt or createdAt timestamp
        const lastLogin = user.lastLoginAt?.toDate?.() || user.createdAt?.toDate?.();
        return lastLogin && lastLogin > ninetyDaysAgo;
      });
      setFilteredUsers(recent);
    } else {
      setFilteredUsers(users);
    }
  }, [users, showOnlyRecent]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);

      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by role (Admin first) then by displayName
      usersList.sort((a, b) => {
        const roleOrder = { admin: 0, moderator: 1, member: 2, guest: 3 };
        const aRole = roleOrder[a.userRole] || 3;
        const bRole = roleOrder[b.userRole] || 3;

        if (aRole !== bRole) return aRole - bRole;
        return (a.displayName || a.email).localeCompare(b.displayName || b.email);
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        userRole: newRole
      });

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, userRole: newRole } : user
      ));

      setEditingUser(null);
      alert('사용자 권한이 업데이트되었습니다.');
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('권한 업데이트 실패: ' + error.message);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);

      // Update local state
      setUsers(users.filter(user => user.id !== userId));
      setDeletingUser(null);
      alert('사용자가 삭제되었습니다.');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('사용자 삭제 실패: ' + error.message);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case USER_ROLES.MODERATOR:
        return <Shield className="w-4 h-4 text-blue-600" />;
      case USER_ROLES.MEMBER:
        return <User className="w-4 h-4 text-gray-600" />;
      default:
        return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case USER_ROLES.MODERATOR:
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case USER_ROLES.MEMBER:
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return '관리자';
      case USER_ROLES.MODERATOR:
        return '운영자';
      case USER_ROLES.MEMBER:
        return '회원';
      default:
        return '게스트';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">사용자 관리</h2>
            <span className="text-sm text-gray-500">
              ({filteredUsers.length}명 표시 / 전체 {users.length}명)
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowOnlyRecent(!showOnlyRecent)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
                showOnlyRecent
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300'
              }`}
            >
              <Filter className="w-4 h-4" />
              {showOnlyRecent ? '최근 활동 사용자만' : '모든 사용자'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Users className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">표시할 사용자가 없습니다</p>
              <p className="text-sm">필터를 변경하여 모든 사용자를 확인하세요</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                        {user.avatar && user.avatar.startsWith('data:image') ? (
                          <img src={user.avatar} alt={user.displayName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl">{user.avatar || '👤'}</span>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {user.displayName || user.email}
                          </h3>
                          {getRoleIcon(user.userRole)}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        {user.username && (
                          <p className="text-xs text-gray-400">@{user.username}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">ID: {user.id.substring(0, 20)}...</p>
                      </div>

                      {/* Current Role Badge */}
                      <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getRoleBadgeColor(user.userRole)}`}>
                        {getRoleLabel(user.userRole)}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                      >
                        권한 변경
                      </button>
                      <button
                        onClick={() => setDeletingUser(user)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="사용자 삭제"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Role Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">권한 변경</h3>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-1">사용자</p>
                <p className="font-semibold text-gray-900">{editingUser.displayName || editingUser.email}</p>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">새 권한 선택</p>
                <div className="space-y-2">
                  {Object.values(USER_ROLES).map(role => (
                    <button
                      key={role}
                      onClick={() => updateUserRole(editingUser.id, role)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition ${
                        editingUser.userRole === role
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {getRoleIcon(role)}
                        <div>
                          <div className="font-semibold text-gray-900">{getRoleLabel(role)}</div>
                          <div className="text-xs text-gray-500">{role}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setEditingUser(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">사용자 삭제</h3>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  다음 사용자를 삭제하시겠습니까?
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{deletingUser.displayName || deletingUser.email}</p>
                  <p className="text-sm text-gray-500">{deletingUser.email}</p>
                  <p className="text-xs text-gray-400 mt-1">ID: {deletingUser.id}</p>
                </div>
                <p className="text-sm text-red-600 mt-3">
                  ⚠️ 이 작업은 되돌릴 수 없습니다. Firestore의 사용자 문서가 영구적으로 삭제됩니다.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingUser(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  취소
                </button>
                <button
                  onClick={() => deleteUser(deletingUser.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
